'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  capitalize,
  withIndefiniteArticle
} from '@paction-2.0/util/formatters';
import { buildHealthScreeningSentence } from './faqData';
import styles from './styles/locationFAQ.module.scss';

export interface LocationFAQProps {
  breedName: string;
  breedSlug: string;
  cityName?: string;
  provinceName: string;
  nicknames?: string[] | null;
  /** Breed-specific screening names to enrich the breeder-selection answer */
  healthScreenings?: string[] | null;
}

export interface FAQItem {
  question: string;
  /** Plain text answer for structured data */
  answerText: string;
  /** React node for rendering (can include links) */
  answerNode: React.ReactNode;
}

/**
 * Generate search-phrase-structured FAQ questions for breed + location pages.
 * Questions and answers are designed to match common search queries with
 * long-tail keywords repeated naturally throughout for SEO value.
 * Works for both city-specific and province-level pages.
 */
export function generateLocationFAQItems(
  breedName: string,
  locationName: string,
  provinceName: string,
  isCity: boolean,
  nicknames?: string[] | null,
  healthScreenings?: string[] | null
): FAQItem[] {
  const healthSentence = buildHealthScreeningSentence(
    breedName,
    healthScreenings
  );
  const locationContext = isCity
    ? `${locationName}, ${provinceName}`
    : locationName;

  const items: FAQItem[] = [];

  if (nicknames && nicknames.length > 0) {
    const nicknameList = nicknames.slice(0, 5).join(', ');
    items.push({
      question: `What is ${withIndefiniteArticle(breedName)} also called?`,
      answerText: `${capitalize(withIndefiniteArticle(breedName))} is also commonly known as ${nicknameList}. These are popular alternative names and search terms used for this breed across Canada.`,
      answerNode: (
        <p>
          {capitalize(withIndefiniteArticle(breedName))} is also commonly known
          as {nicknameList}. These are popular alternative names and search
          terms used for this breed across Canada.
        </p>
      )
    });
  }

  items.push(
    {
      question: `How much do ${breedName} puppies cost in ${locationName}?`,
      answerText: `${breedName} puppy prices in ${locationContext} typically vary based on factors like breeder reputation, health testing, lineage and breeding rights. Paction recommends contacting breeders directly through our platform to get current pricing. Always prioritize breeders who provide health guarantees and proper documentation. It's also important to budget for the ongoing costs of owning a dog. For that, Paction has developed The Ultimate Dog Cost Calculator.`,
      answerNode: (
        <>
          <p>
            {breedName} puppy prices in {locationContext} typically vary based
            on factors like breeder reputation, health testing, lineage and
            breeding rights. Paction recommends contacting breeders directly
            through our platform to get current pricing. Always prioritize
            breeders who provide health guarantees and proper documentation.
          </p>
          <p>
            It&apos;s also important to budget for the ongoing costs of owning a
            dog. For that, Paction has developed{' '}
            <Link href="/dog-cost-calculator">
              The Ultimate Dog Cost Calculator
            </Link>
            .
          </p>
        </>
      )
    },
    {
      question: `Where can I find ${breedName} breeders near ${locationName}?`,
      answerText: `You can find reputable ${breedName} breeders near ${locationName} right here on Paction. Paction features verified breeders throughout ${provinceName} who raise healthy, well-socialized puppies. Join the ${breedName} waitlist and Paction will start matching you with trusted breeders across Canada, reaching out as we connect you with breeders who fit what you're looking for.`,
      answerNode: (
        <p>
          You can find reputable {breedName} breeders near {locationName} right
          here on Paction. Paction features verified breeders throughout{' '}
          {provinceName} who raise healthy, well-socialized puppies. Join the{' '}
          {breedName} waitlist and Paction will start matching you with trusted
          breeders across Canada, reaching out as we connect you with breeders
          who fit what you&apos;re looking for.
        </p>
      )
    },
    {
      question: `What happens after I join the ${breedName} waitlist in ${locationName}?`,
      answerText: `When you join the ${breedName} waitlist for ${locationContext}, Paction starts matching you with trusted breeders across Canada who raise ${breedName}. Responsible breeders plan their litters intentionally, so joining lets Paction connect you with the right breeder when the timing is right, with no pressure and no rushed decisions. You can message breeders directly through Paction and arrange visits or video calls.`,
      answerNode: (
        <p>
          When you join the {breedName} waitlist for {locationContext}, Paction
          starts matching you with trusted breeders across Canada who raise{' '}
          {breedName}. Responsible breeders plan their litters intentionally, so
          joining lets Paction connect you with the right breeder when the
          timing is right, with no pressure and no rushed decisions. You can
          message breeders directly through Paction and arrange visits or video
          calls.
        </p>
      )
    },
    {
      question: `What should I look for in ${withIndefiniteArticle(breedName)} breeder in ${locationName}?`,
      answerText: `When choosing ${withIndefiniteArticle(breedName)} breeder in ${locationName}, look for breeders who perform health testing on parent dogs, provide health guarantees, socialize puppies from an early age, and welcome visits to their breeding program.${healthSentence ? ` ${healthSentence}` : ''} Reputable breeders will be transparent about their breeding practices and happy to answer your questions. To better understand why health testing on the parent dogs is important, check out Paction's Making Science Simple: Canine Genetics for Puppy Shoppers. For more educational information, explore Expert Hub.`,
      answerNode: (
        <>
          <p>
            When choosing {withIndefiniteArticle(breedName)} breeder in{' '}
            {locationName}, look for breeders who perform health testing on
            parent dogs, provide health guarantees, socialize puppies from an
            early age, and welcome visits to their breeding program.
            {healthSentence && <> {healthSentence}</>} Reputable breeders will
            be transparent about their breeding practices and happy to answer
            your questions.
          </p>
          <p>
            To better understand why health testing on the parent dogs is
            important, check out Paction&apos;s{' '}
            <Link href="/canine-genetics-for-puppy-shoppers">
              Making Science Simple: Canine Genetics for Puppy Shoppers
            </Link>
            . For more educational information, explore{' '}
            <Link href="/expert-hub">Expert Hub</Link>.
          </p>
        </>
      )
    },
    {
      question: `Can I visit ${breedName} breeders in ${locationName} before buying?`,
      answerText: `Most reputable ${breedName} breeders in ${locationName} and the surrounding ${provinceName} area welcome visits or video calls from potential puppy parents. Meeting the breeder, seeing their facility, and interacting with the parent dogs and puppies is an important part of the process. Contact breeders through Paction to arrange a visit and ask any questions you have about their puppies.`,
      answerNode: (
        <p>
          Most reputable {breedName} breeders in {locationName} and the
          surrounding {provinceName} area welcome visits or video calls from
          potential puppy parents. Meeting the breeder, seeing their facility,
          and interacting with the parent dogs and puppies is an important part
          of the process. Contact breeders through Paction to arrange a visit
          and ask any questions you have about their puppies.
        </p>
      )
    },
    {
      question: `How can I avoid puppy scams when looking for ${withIndefiniteArticle(breedName)} in ${locationName}?`,
      answerText: `Puppy scams are unfortunately common when searching for popular breeds like ${breedName} in ${locationName}. Red flags include breeders unwilling to show health testing, pressure to send deposits quickly, inconsistent information, or puppies offered without clear planning details. Paction reduces scam risk by working with breeders who verify their identities and commit to transparent, documented breeding practices, giving puppy buyers a safer starting point when searching locally.`,
      answerNode: (
        <p>
          Puppy scams are unfortunately common when searching for popular breeds
          like {breedName} in {locationName}. Red flags include breeders
          unwilling to show health testing, pressure to send deposits quickly,
          inconsistent information, or puppies offered without clear planning
          details. Paction reduces scam risk by working with breeders who verify
          their identities and commit to transparent, documented breeding
          practices, giving puppy buyers a safer starting point when searching
          locally.
        </p>
      )
    }
  );

  return items;
}

/**
 * LocationFAQ component displays location-specific FAQ content for breed pages.
 * Works for both city-specific and province-level pages.
 * Designed to capture long-tail search traffic and provide genuine value to users.
 */
export function LocationFAQ({
  breedName,
  nicknames,
  cityName,
  provinceName,
  healthScreenings,
  includeStructuredData = true
}: LocationFAQProps & { includeStructuredData?: boolean }) {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const isCity = Boolean(cityName);
  const locationName = cityName || provinceName;
  const faqItems = generateLocationFAQItems(
    breedName,
    locationName,
    provinceName,
    isCity,
    nicknames,
    healthScreenings
  );

  const toggleItem = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <section
      className={styles.locationFaq}
      aria-labelledby="location-faq-title"
    >
      <h2 id="location-faq-title" className={styles.title}>
        Frequently Asked Questions About {breedName} Puppies in {locationName}
      </h2>
      <div className={styles.faqList}>
        {faqItems.map((item, index) => (
          <div
            key={index}
            className={`${styles.faqItem} ${expandedIndex === index ? styles.faqItemExpanded : ''}`}
          >
            <button
              type="button"
              className={styles.questionButton}
              onClick={() => toggleItem(index)}
              aria-expanded={expandedIndex === index}
              aria-controls={`faq-answer-${index}`}
            >
              <h3 className={styles.question}>{item.question}</h3>
              <span className={styles.chevron} aria-hidden="true" />
            </button>
            <div
              id={`faq-answer-${index}`}
              className={styles.answer}
              hidden={expandedIndex !== index}
            >
              {item.answerNode}
            </div>
          </div>
        ))}
      </div>

      {/* JSON-LD FAQ structured data for SEO - only rendered if includeStructuredData is true */}
      {includeStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: item.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: item.answerText
                }
              }))
            })
          }}
        />
      )}
    </section>
  );
}
